import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { MainLayout } from './components/layout/MainLayout'
import { HomeSection } from './components/sections/Home'
import { AboutSection } from './components/sections/About'
import { ExperienceSection } from './components/sections/Experience'
import { SkillsSection } from './components/sections/Skills'
import { ProjectsSection } from './components/sections/Projects'
import { BlogList, BlogPost } from './components/sections/Blog'
import { ConfigProvider } from './config/ConfigProvider'
import { ThemeProvider } from './contexts/ThemeContext'
import SeoHead from './components/common/SeoHead'
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'))
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'))

// Admin route path — single source of truth
const ADMIN_PATH = '/akrami'

const pageTransition = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: 'easeInOut' }
}

const AnimatedPage = ({ children }) => (
  <motion.div {...pageTransition}>
    {children}
  </motion.div>
)

// Internal component to manage layouts based on current route
const AppRoutes = () => {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith(ADMIN_PATH)

  if (isAdminRoute) {
    return (
      <Suspense fallback={<div style={{padding:20}}>Loading...</div>}>
        <Routes>
          <Route path={`${ADMIN_PATH}`} element={<AdminDashboard />} />
          <Route path={`${ADMIN_PATH}/dashboard`} element={<AdminDashboard />} />
          <Route path={`${ADMIN_PATH}/login`} element={<AdminLogin />} />
          <Route path={`${ADMIN_PATH}/*`} element={<Navigate to={ADMIN_PATH} replace />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <MainLayout>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<AnimatedPage><HomeSection /></AnimatedPage>} />
          <Route path="/about-me" element={<AnimatedPage><AboutSection /></AnimatedPage>} />
          <Route path="/experience" element={<AnimatedPage><ExperienceSection /></AnimatedPage>} />
          <Route path="/skills" element={<AnimatedPage><SkillsSection /></AnimatedPage>} />
          <Route path="/projects" element={<AnimatedPage><ProjectsSection /></AnimatedPage>} />
          <Route path="/blog" element={<AnimatedPage><BlogList /></AnimatedPage>} />
          <Route path="/blog/:slug" element={<AnimatedPage><BlogPost /></AnimatedPage>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </MainLayout>
  )
}

const App = () => {
  return (
    <ThemeProvider>
      <ConfigProvider>
        <BrowserRouter>
          <SeoHead />
          <AppRoutes />
        </BrowserRouter>
      </ConfigProvider>
    </ThemeProvider>
  )
}

export default App